import React from 'react';
import { Input } from 'src/domains/common/components/input/input';
import { Select } from 'src/domains/common/components/select/select';
import { observer } from 'src/utils/mobx-react';
import { useCommon } from 'src/AppState/Common';
import { CampaignCreatorState } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.state';
import { PlayThroughPicker } from 'src/domains/casino/webview/bonus-engine/common/components/PlayThroughPicker';
import { FreeSpinsPicker } from 'src/domains/casino/webview/bonus-engine/common/components/FreeSpinsPicker';
import { Calendar } from 'src/domains/casino/webview/bonus-engine/campaign/Calendar';
import {
    formatNumericInput,
    UK_TIMEZONE,
} from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.helpers';
import { SchedulingSection } from 'src/domains/casino/webview/bonus-engine/common/components/SchedulingSection';
import { EXPIRY_OPTIONS } from 'src/domains/casino/webview/bonus-engine/common/const';

interface CasinoSectionStatePropsType {
    campaignId?: string;
}

export const CasinoSection = observer('CasinoSection', ({ campaignId }: CasinoSectionStatePropsType): JSX.Element => {
    const common = useCommon();
    const state = CampaignCreatorState.get(common, campaignId);
    const {
        rewardConditionsState: { casinoSectionState },
        editionDisabled,
    } = state;
    const {
        relativeExpiryDays,
        endDateTimeForm,
        allRewardCriteriaOptions,
        expiryForm,
        typeForm,
        totalBetForm,
        freeSpinsEligibleGamesPickerForm,
        playThroughForm,
        numberOfFreeSpinsForm,
    } = casinoSectionState;

    return (
        <>
            <section
                data-test='reward-expiry'
                className='flex flex-col gap-6'
            >
                <h2 className='m-0 text-[1rem] font-semibold leading-[1.75]'>Reward Expiry</h2>
                <div className='grid grid-cols-2 gap-4'>
                    <Select
                        label='Expiry'
                        placeholder='Select'
                        values={EXPIRY_OPTIONS}
                        formInputState={expiryForm}
                        errors={expiryForm.errorsForView}
                        disabled={editionDisabled}
                    />
                    {expiryForm.value === 'relative-expiry' ? (
                        <Input
                            label='Number of days'
                            placeholder='Number'
                            formInputState={relativeExpiryDays}
                            description='Enter a number greater than 0'
                            errors={relativeExpiryDays.errorsForView}
                            disabled={editionDisabled}
                        />
                    ) : (
                        <Calendar
                            description='After campaign end'
                            label='End Date & Time (UK time)'
                            firstDayOfWeek='monday'
                            timezone={UK_TIMEZONE}
                            formInputState={endDateTimeForm}
                            placeholder='*Current Date*'
                            showTimePicker={true}
                            errors={endDateTimeForm.errorsForView}
                            disabled={editionDisabled}
                        />
                    )}
                </div>
            </section>

            <section
                data-test='reward-criteria'
                className='flex flex-col gap-6'
            >
                <h2 className='m-0 text-[1rem] font-semibold leading-[1.75]'>Reward Criteria</h2>
                <div className='flex flex-col gap-4'>
                    <div className='grid grid-cols-2 gap-4'>
                        <Select
                            label='Type'
                            placeholder='Select'
                            values={allRewardCriteriaOptions}
                            formInputState={typeForm}
                            errors={typeForm.errorsForView}
                            disabled={editionDisabled}
                        />

                        <Input
                            label='Number of Free Spins'
                            placeholder='Type'
                            formInputState={numberOfFreeSpinsForm}
                            description='Enter a whole number'
                            errors={numberOfFreeSpinsForm.errorsForView}
                            disabled={editionDisabled}
                        />
                    </div>

                    <div className='flex flex-col gap-4'>
                        <Input
                            label='Total Bet'
                            placeholder='Type'
                            formInputState={totalBetForm}
                            description='Enter a decimal value. Between 0.01 and 10.00'
                            errors={totalBetForm.errorsForView}
                            onBlur={() => formatNumericInput(totalBetForm)}
                            disabled={editionDisabled}
                        />
                    </div>

                    <FreeSpinsPicker
                        state={state}
                        errors={freeSpinsEligibleGamesPickerForm.errorsForView}
                        disabled={editionDisabled}
                    />
                </div>
            </section>

            <SchedulingSection
                state={casinoSectionState}
                editionDisabled={editionDisabled}
            />

            <section
                data-test='playtrough-criteria'
                className='flex flex-col gap-6'
            >
                <h2 className='m-0 text-[1rem] font-semibold leading-[1.75]'>Playthrough Criteria</h2>
                <div className='flex flex-col gap-4'>
                    <Input
                        label='Playthrough'
                        placeholder='Type'
                        formInputState={playThroughForm}
                        description='Enter a decimal value. Between 0.0 and 50.0'
                        errors={playThroughForm.errorsForView}
                        onBlur={() => formatNumericInput(playThroughForm)}
                        disabled={editionDisabled}
                    />

                    <PlayThroughPicker
                        state={state}
                        disabled={editionDisabled}
                    />
                </div>
            </section>
        </>
    );
});
